/**
 * KPI / stat tile — a big number plus an uppercase micro-label.
 *
 * Built to serve both Dashboard metrics and Team KPIs from one component.
 * Public interface: `value` + `label` (+ optional `icon`, `tone`, `layout`).
 * Hides the tone→colour mapping and the center/row layout switch.
 *
 * Props:
 *   value     — the metric (node; e.g. "95%", a count, or "…" while loading)
 *   label     — uppercase micro-label beneath/beside the value
 *   icon      — optional leading icon node
 *   tone      — 'default' | 'success' | 'warning' | 'danger' | 'brand'
 *               (drives the value colour — compliance level + repairs accent)
 *   layout    — 'center' (KPI/Team) | 'row' (icon + value/label)
 *   className — extra class(es) on the tile
 *
 * All other props (e.g. data-level) pass through to the tile element.
 */
const TONE_CLASSES = {
  default: '',
  success: 'stat-tile--success',
  warning: 'stat-tile--warning',
  danger: 'stat-tile--danger',
  brand: 'stat-tile--brand',
};

export default function StatTile({
  value,
  label,
  icon,
  tone = 'default',
  layout = 'center',
  className = '',
  ...props
}) {
  const toneClass = TONE_CLASSES[tone] || '';
  const classes = ['stat-tile', `stat-tile--${layout}`, toneClass, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} {...props}>
      {icon && (
        <span className="stat-tile-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="stat-tile-body">
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-label">{label}</span>
      </span>
    </div>
  );
}
